import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Check, Sprout, Bird, Sparkles as Galaxy, Loader2, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { CurrencySwitch } from "@/components/CurrencySwitch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SpiralCinematic } from "@/components/SpiralCinematic";
import { CinematicVideo } from "@/components/CinematicVideo";
import { Footer } from "@/components/Footer";
import { CocoonMark } from "@/components/CocoonMark";

const META = [
  { id: "souffle", icon: Sprout, monthly: 0, highlight: false },
  { id: "elan", icon: Bird, monthly: 7, highlight: true },
  { id: "cocon", icon: Galaxy, monthly: 15, highlight: false },
];

const CHANGE_LABELS = {
  fr: { current: "Formule actuelle", upgrade: "Passer à", downgrade: "Revenir à", toAnnual: "Passer à l'annuel", toMonthly: "Passer au mensuel", changed: "Ton forfait a été mis à jour ✨", error: "Changement impossible pour le moment." },
  en: { current: "Current plan", upgrade: "Switch to", downgrade: "Switch to", toAnnual: "Switch to yearly", toMonthly: "Switch to monthly", changed: "Your plan was updated ✨", error: "Change unavailable right now." },
  es: { current: "Plan actual", upgrade: "Cambiar a", downgrade: "Cambiar a", toAnnual: "Cambiar a anual", toMonthly: "Cambiar a mensual", changed: "Tu plan fue actualizado ✨", error: "Cambio no disponible ahora mismo." },
  nl: { current: "Huidig plan", upgrade: "Overstappen naar", downgrade: "Overstappen naar", toAnnual: "Naar jaarlijks", toMonthly: "Naar maandelijks", changed: "Je plan is bijgewerkt ✨", error: "Wijzigen kan nu niet." },
};
const RANK = { souffle: 0, elan: 1, cocon: 2 };

export default function Tarifs() {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const { convert, symbol, rate } = useCurrency();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [sub, setSub] = useState(null);
  const [changingPlan, setChangingPlan] = useState(null);
  const L = CHANGE_LABELS[(i18n.language || "fr").slice(0, 2)] || CHANGE_LABELS.fr;
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(META.findIndex((m) => m.highlight));
  const [isDesktop, setIsDesktop] = useState(false);
  const selectedRef = useRef(null);
  const firstSelect = useRef(true);

  useEffect(() => {
    if (firstSelect.current) { firstSelect.current = false; return; }
    if (!isDesktop && selectedRef.current) {
      selectedRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selected]); // eslint-disable-line

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const on = () => setIsDesktop(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const annualTotal = (m) => Math.round(m * 12 * 0.7); // -30%
  const annualPerMonth = (m) => (annualTotal(m) / 12).toFixed(2).replace(".", ",");

  const startCheckout = useCallback(async (planId, isAnnual) => {
    // Flux Checkout intégré (Stripe managé Emergent) — attribution auto au retour, sans webhook.
    if (!user) {
      localStorage.setItem("coccon_checkout_intent", `${planId}_${isAnnual ? "yearly" : "monthly"}`);
      navigate("/entrer");
      return;
    }
    localStorage.removeItem("coccon_checkout_intent");
    const lookup_key = `${planId}_${isAnnual ? "yearly" : "monthly"}`;
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("no_url");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Le paiement n'est pas disponible pour le moment. Réessaie dans un instant.");
      setLoadingPlan(null);
    }
  }, [user, navigate]);

  // resume checkout after login
  useEffect(() => {
    const intent = localStorage.getItem("coccon_checkout_intent");
    if (user && intent) {
      const [planId, interval] = intent.split("_");
      if (["elan", "cocon"].includes(planId)) {
        setAnnual(interval === "yearly");
        startCheckout(planId, interval === "yearly");
      }
    }
  }, [user, startCheckout]);

  const handleCta = (meta) => {
    if (meta.monthly === 0) { navigate("/entrer"); return; }
    startCheckout(meta.id, annual);
  };

  // Load current subscription so we can offer in-app upgrade/downgrade.
  const loadSub = useCallback(async () => {
    if (!user) { setSub(null); return; }
    try {
      const { data } = await api.get("/payments/subscription");
      setSub(data?.subscription || null);
    } catch {
      setSub(null);
    }
  }, [user]);

  useEffect(() => { loadSub(); }, [loadSub]);

  const changePlan = useCallback(async (lookupKey, planId) => {
    setChangingPlan(planId);
    try {
      await api.post("/payments/subscription/change", { lookup_key: lookupKey });
      toast.success(L.changed);
      await refresh();
      await loadSub();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || L.error);
    } finally {
      setChangingPlan(null);
    }
  }, [refresh, loadSub, L]);

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-x-clip">
      <div className="grain-overlay" aria-hidden />
      <div className="amber-glow" aria-hidden style={{ top: "-8%", left: "50%", transform: "translateX(-50%)", width: "880px", height: "640px" }} />
      <div className="amber-glow" aria-hidden style={{ top: "60%", right: "-14%", left: "auto", width: "620px", height: "620px", opacity: 0.6 }} />
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl tracking-tight whitespace-nowrap" data-testid="tarifs-brand"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/manifeste" className="text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.manifeste")}</Link>
            <Link to="/echo" className="text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.echo")}</Link>
            <Link to="/faq" className="hidden sm:inline text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.faq")}</Link>
            <Link to="/entrer" data-testid="tarifs-enter-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-36 pb-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">{t("tarifs.eyebrow")}</p>
          <div className="max-w-2xl mx-auto mb-8">
            <CinematicVideo
              fade
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/atxyr66s_gemini_generated_video_14f45593.mp4"
              testId="tarifs-cinematic-video"
              caption={`${t("tarifs.titleA")}${t("tarifs.titleHi")}${t("tarifs.titleB")}`}
            />
          </div>
          <p className="text-foreground/60 leading-relaxed text-lg">{t("tarifs.subtitle")}</p>

          <div className="relative inline-flex items-center mt-8 p-1 rounded-full glass" data-testid="billing-toggle">
            <button
              data-testid="billing-monthly"
              onClick={() => setAnnual(false)}
              className="relative z-10 px-5 py-2 rounded-full text-sm font-medium"
            >
              {!annual && (
                <motion.span layoutId="billing-pill" className="absolute inset-0 -z-10 rounded-full bg-primary"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }} />
              )}
              <span className={`transition-colors ${!annual ? "text-primary-foreground" : "text-foreground/60"}`}>{t("tarifs.monthly")}</span>
            </button>
            <button
              data-testid="billing-annual"
              onClick={() => setAnnual(true)}
              className="relative z-10 px-5 py-2 rounded-full text-sm font-medium flex items-center gap-2"
            >
              {annual && (
                <motion.span layoutId="billing-pill" className="absolute inset-0 -z-10 rounded-full bg-primary"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }} />
              )}
              <span className={`transition-colors ${annual ? "text-primary-foreground" : "text-foreground/60"}`}>{t("tarifs.annual")}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${annual ? "bg-primary-foreground/25 text-primary-foreground" : "bg-primary/20 text-primary"}`}>{t("tarifs.save")}</span>
            </button>
          </div>
          <div className="mt-5 flex justify-center">
            <CurrencySwitch />
          </div>
        </div>

        <div className="relative">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10" style={{ background: "radial-gradient(ellipse at 50% 45%, hsl(28 96% 56% / 0.14), transparent 62%)" }} />
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-[0.06] mix-blend-soft-light" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-70" style={{ backgroundImage: "radial-gradient(1px 1px at 12% 22%, hsl(40 30% 90% / 0.7), transparent), radial-gradient(1px 1px at 70% 15%, hsl(35 80% 70% / 0.6), transparent), radial-gradient(1.5px 1.5px at 85% 40%, hsl(40 30% 90% / 0.5), transparent), radial-gradient(1px 1px at 25% 62%, hsl(40 30% 90% / 0.5), transparent), radial-gradient(1px 1px at 55% 82%, hsl(35 80% 70% / 0.5), transparent), radial-gradient(1px 1px at 92% 74%, hsl(40 30% 90% / 0.4), transparent)" }} />
          <svg aria-hidden className="hidden md:block pointer-events-none absolute inset-x-0 top-8 -z-10" width="100%" height="170" viewBox="0 0 1000 170" preserveAspectRatio="none">
            <defs>
              <linearGradient id="cocoon-constellation" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="hsl(28 96% 56%)" stopOpacity="0" />
                <stop offset="50%" stopColor="hsl(28 96% 56%)" stopOpacity="0.55" />
                <stop offset="100%" stopColor="hsl(28 96% 56%)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <polyline points="180,118 500,58 820,118" fill="none" stroke="url(#cocoon-constellation)" strokeWidth="1.5" strokeDasharray="2 7" strokeLinecap="round" />
            <circle cx="180" cy="118" r="3.5" fill="hsl(28 96% 56%)" className="constellation-twinkle" />
            <circle cx="500" cy="58" r="5" fill="hsl(28 96% 56%)" className="constellation-twinkle" style={{ animationDelay: "0.6s" }} />
            <circle cx="820" cy="118" r="3.5" fill="hsl(28 96% 56%)" className="constellation-twinkle" style={{ animationDelay: "1.2s" }} />
          </svg>
          <svg aria-hidden className="md:hidden pointer-events-none absolute left-7 top-14 bottom-14 -z-10" width="26" viewBox="0 0 26 900" preserveAspectRatio="none">
            <polyline points="13,60 13,450 13,840" fill="none" stroke="hsl(28 96% 56%)" strokeOpacity="0.4" strokeWidth="1.5" strokeDasharray="2 9" strokeLinecap="round" />
            <circle cx="13" cy="60" r="4" fill="hsl(28 96% 56%)" className="constellation-twinkle" />
            <circle cx="13" cy="450" r="5" fill="hsl(28 96% 56%)" className="constellation-twinkle" style={{ animationDelay: "0.6s" }} />
            <circle cx="13" cy="840" r="4" fill="hsl(28 96% 56%)" className="constellation-twinkle" style={{ animationDelay: "1.2s" }} />
          </svg>

          <div
            className="flex flex-col md:flex-row items-center md:items-stretch justify-center gap-5 md:gap-5 pt-6 md:pt-16 pb-8"
            data-testid="tarifs-eventail"
            style={{ perspective: 1400 }}
            onMouseLeave={() => setHovered(null)}
          >
            {META.map((meta, i) => {
              const tier = t(`tiers.${meta.id}`, { returnObjects: true });
              const Icon = meta.icon;
              const isFree = meta.monthly === 0;
              const price = isFree ? 0 : annual ? annualTotal(meta.monthly) : meta.monthly;
              const periodLabel = isFree ? t("tarifs.forever") : annual ? t("tarifs.perYear") : t("tarifs.perMonth");
              // In-app upgrade/downgrade for users with an active paid subscription.
              const targetLookup = isFree ? null : `${meta.id}_${annual ? "yearly" : "monthly"}`;
              const curLookup = sub?.lookup_key || null;
              const curPlan = curLookup ? curLookup.split("_")[0] : null;
              const isCurrent = !!(curLookup && targetLookup && curLookup === targetLookup);
              const isChanging = changingPlan === meta.id;
              let ctaLabel = tier.cta;
              let ctaDisabled = loadingPlan === meta.id;
              let ctaOnClick = () => handleCta(meta);
              let ctaLoading = loadingPlan === meta.id;
              if (sub && !isFree) {
                if (isCurrent) {
                  ctaLabel = L.current; ctaDisabled = true; ctaOnClick = undefined; ctaLoading = false;
                } else if (curPlan === meta.id) {
                  ctaLabel = annual ? L.toAnnual : L.toMonthly;
                  ctaOnClick = () => changePlan(targetLookup, meta.id);
                  ctaDisabled = isChanging; ctaLoading = isChanging;
                } else {
                  const up = (RANK[meta.id] ?? 0) > (RANK[curPlan] ?? 0);
                  ctaLabel = `${up ? L.upgrade : L.downgrade} ${tier.name}`;
                  ctaOnClick = () => changePlan(targetLookup, meta.id);
                  ctaDisabled = isChanging; ctaLoading = isChanging;
                }
              }
              const FAN = [
                { rotate: -3, y: 8, z: 10, opacity: 1 },
                { rotate: 0, y: -8, z: 20, opacity: 1 },
                { rotate: 3, y: 8, z: 10, opacity: 1 },
              ][i];
              const isHot = hovered === i;
              const isSelected = selected === i;
              const mobileCollapsed = false;
              const anim = isDesktop
                ? {
                    rotate: isHot ? 0 : FAN.rotate,
                    y: isHot ? FAN.y - 16 : FAN.y,
                    scale: isHot ? 1.04 : 1,
                    opacity: 1,
                    rotateX: 0,
                  }
                : { rotate: 0, y: 0, scale: 1, opacity: 1, rotateX: 0 };
              return (
                <motion.div
                  key={meta.id}
                  ref={!isDesktop && isSelected ? selectedRef : undefined}
                  data-testid={`tier-${meta.id}`}
                  onHoverStart={() => setHovered(i)}
                  onClick={mobileCollapsed ? () => setSelected(i) : undefined}
                  initial={{ opacity: 0, y: 60, rotateX: 14 }}
                  animate={anim}
                  transition={
                    !isDesktop && isSelected
                      ? { type: "spring", stiffness: 300, damping: 14 }
                      : { type: "spring", stiffness: 200, damping: 22 }
                  }
                  style={{
                    zIndex: isDesktop ? (isHot ? 40 : FAN.z) : FAN.z,
                    transformOrigin: "bottom center",
                    backgroundImage: "radial-gradient(circle at 50% 24%, hsl(28 96% 56% / 0.40), hsl(24 95% 52% / 0.14) 42%, transparent 72%)",
                    boxShadow: isHot || (!isDesktop && meta.highlight)
                      ? "0 46px 100px -22px rgba(0,0,0,0.8), 0 0 60px -12px hsl(28 96% 56% / 0.35)"
                      : "0 22px 60px -26px rgba(0,0,0,0.65)",
                  }}
                  className={`relative glass rounded-[28px] flex flex-col w-full max-w-sm md:w-[326px] ${mobileCollapsed ? "p-5 cursor-pointer" : "p-8"} ${meta.highlight ? "ring-1 ring-primary/50 frame-pulse" : "ring-1 ring-white/10"}`}
                >
                  {meta.highlight && !mobileCollapsed && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full bg-primary text-primary-foreground font-semibold whitespace-nowrap shadow-[0_0_18px_hsl(28_96%_56%_/_0.5)]">{t("tarifs.popular")}</span>
                  )}

                  {mobileCollapsed ? (
                    <div className="flex items-center gap-3 w-full" data-testid={`tier-collapsed-${meta.id}`}>
                      <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 shadow-[0_0_14px_hsl(28_96%_56%_/_0.3)] ring-1 ring-primary/25">
                        <Icon className="text-primary" size={20} strokeWidth={1.6} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display text-xl leading-tight">{tier.name}</h3>
                        <p className="text-foreground/45 text-xs italic font-display truncate">{tier.tagline}</p>
                      </div>
                      <div className="flex items-baseline gap-0.5 flex-shrink-0">
                        <span className="font-display text-2xl text-primary">{convert(price)}</span>
                        <span className="text-primary text-sm">{symbol}</span>
                      </div>
                      <ChevronDown size={16} className="text-foreground/30 flex-shrink-0" />
                    </div>
                  ) : (
                    <>
                      <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mb-5 shadow-[0_0_22px_hsl(28_96%_56%_/_0.35)] ring-1 ring-primary/30">
                        <Icon className="text-primary" size={26} strokeWidth={1.6} />
                      </div>
                      <h3 className="font-display text-3xl mb-1">{tier.name}</h3>
                      <p className="text-foreground/50 mb-6 italic font-display text-lg">{tier.tagline}</p>
                      <div className="mb-1 flex items-baseline gap-1">
                        <span className="font-display text-5xl text-primary" data-testid={`price-${meta.id}`}>{convert(price)} {symbol}</span>
                        <span className="text-foreground/40 text-sm">/ {periodLabel}</span>
                      </div>
                      <p className="text-xs text-foreground/40 mb-7 h-4">
                        {annual && !isFree ? t("tarifs.billedAnnually", { m: `${((annualTotal(meta.monthly) * rate) / 12).toFixed(2).replace(".", ",")} ${symbol}` }) : ""}
                      </p>
                      <ul className="space-y-3 mb-8 flex-1">
                        {tier.features.map((f) => (
                          <li key={f} className="flex items-start gap-3 text-foreground/70 text-sm leading-relaxed">
                            <Check size={16} className="text-primary mt-0.5 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                      <button onClick={ctaOnClick} disabled={ctaDisabled} data-testid={`tier-cta-${meta.id}`}
                        className={`${meta.highlight ? "btn-frost-orange" : "border border-primary/40 text-primary hover:bg-primary/10 transition-colors"} text-center py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 ${isCurrent ? "cursor-default" : ""}`}>
                        {ctaLoading && <Loader2 size={16} className="animate-spin" />}
                        {ctaLabel}
                      </button>
                    </>
                  )}
                </motion.div>
              );
            })}
          </div>
          <p className="hidden md:block text-center text-foreground/35 text-xs mt-2" data-testid="tarifs-eventail-hint">{t("tarifs.eventailHint")}</p>
          <p className="md:hidden text-center text-foreground/35 text-xs mt-4" data-testid="tarifs-eventail-hint-mobile">{t("tarifs.eventailHintMobile")}</p>
        </div>

        <p className="text-center text-foreground/40 text-sm mt-14 max-w-lg mx-auto leading-relaxed">{t("tarifs.note")}</p>
      </section>

      <Footer />
    </div>
  );
}
